import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';

import {
  UserGroup,
  UserGroupAssignmentRequestDTO,
  UserGroupPermissionResponseDTO,
} from '@pulpo/pulpo-models';
import { APP_CONFIG } from '@pulpo/app-config';
import ApiService from './api.service.abstract';

@Injectable({ providedIn: 'root' })
export class UserGroupService extends ApiService<UserGroup> {
  constructor(@Inject(APP_CONFIG) environment: any, private http: HttpClient) {
    super(environment, http, 'user/api/user-groups');
    if (environment.name === 'localDev') {
      this.url = 'http://localhost:8095';
    }
  }

  getProducerGroup(userId: string): Observable<HttpResponse<UserGroup>> {
    return this.http.get<UserGroup>(`${this.url}/${this.endpoint}/producer`, {
      params: { userId },
      observe: 'response',
    });
  }

  getAllGroups(isProducer: boolean): Observable<HttpResponse<UserGroup[]>> {
    return this.http.get<UserGroup[]>(`${this.url}/${this.endpoint}/`, {
      params: { isProducer },
      observe: 'response',
    });
  }

  /**
   * Get all available groups for selection in UI
   */
  getAvailableGroups(): Observable<HttpResponse<UserGroup[]>> {
    return this.http.get<UserGroup[]>(
      `${this.url}/${this.endpoint}/available`,
      {
        observe: 'response',
      }
    );
  }

  /**
   * Add a user to a group with specific permissions
   */
  addUserToGroup(
    userId: string,
    request: UserGroupAssignmentRequestDTO
  ): Observable<HttpResponse<void>> {
    return this.http.post<void>(
      `${this.url}/${this.endpoint}/${userId}/add`,
      request,
      {
        observe: 'response',
      }
    );
  }

  /**
   * Remove a user from a group
   */
  removeUserFromGroup(
    userId: string,
    groupId: string
  ): Observable<HttpResponse<void>> {
    return this.http.delete<void>(
      `${this.url}/${this.endpoint}/${userId}/${groupId}`,
      {
        observe: 'response',
      }
    );
  }

  /**
   * Update permissions for a user in a group
   */
  updateGroupPermissions(
    userId: string,
    groupId: string,
    request: UserGroupAssignmentRequestDTO
  ): Observable<HttpResponse<void>> {
    return this.http.put<void>(
      `${this.url}/${this.endpoint}/${userId}/${groupId}/permissions`,
      request,
      {
        observe: 'response',
      }
    );
  }

  /**
   * Get all groups and permissions for a specific user
   */
  getUserGroups(
    userId: string
  ): Observable<HttpResponse<UserGroupPermissionResponseDTO[]>> {
    return this.http.get<UserGroupPermissionResponseDTO[]>(
      `${this.url}/${this.endpoint}/${userId}/groups`,
      {
        observe: 'response',
      }
    );
  }
}
